/**
 * 197 (contracts/push-repair.md): the pure decision core plus the repair sequence both entry
 * points share — the silent hook (`usePushRegistrationRepair.ts`) and the explicit "Enable"
 * button (`PushNotificationsSettings.tsx`).
 */

export type RepairResult = 'no-registration' | 'current' | 'superseded' | 'indeterminate';

export interface ExistingSubscriptionKey {
  applicationServerKey?: ArrayBuffer | null;
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function bytesEqual(a: ArrayBuffer, b: ArrayBuffer): boolean {
  if (a.byteLength !== b.byteLength) return false;
  const av = new Uint8Array(a);
  const bv = new Uint8Array(b);
  for (let i = 0; i < av.length; i++) {
    if (av[i] !== bv[i]) return false;
  }
  return true;
}

/**
 * Pure: no service worker, no network, no clock. `existing` is `null` when there is no
 * subscription at all — distinct from a subscription whose `applicationServerKey` cannot be
 * read, which is `indeterminate` (research.md §2). `indeterminate` must never collapse into
 * `current` or `superseded`.
 */
export function needsRepair(
  existing: ExistingSubscriptionKey | null,
  publishedKey: string,
): RepairResult {
  if (!existing) return 'no-registration';
  const key = existing.applicationServerKey;
  if (!key) return 'indeterminate';
  const published = urlBase64ToUint8Array(publishedKey).buffer as ArrayBuffer;
  return bytesEqual(key, published) ? 'current' : 'superseded';
}

export interface PushRegistrationDeps {
  apiBaseUrl: string;
  accessToken: string;
}

/** Registers a fresh subscription against the currently published key (FR-006). */
export async function subscribeAndRegister(
  registration: ServiceWorkerRegistration,
  publishedKey: string,
  deps: PushRegistrationDeps,
): Promise<void> {
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publishedKey).buffer as ArrayBuffer,
  });

  const subJson = subscription.toJSON() as {
    endpoint: string;
    keys?: { p256dh?: string; auth?: string };
  };

  const res = await fetch(`${deps.apiBaseUrl}/push-subscriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${deps.accessToken}`,
    },
    body: JSON.stringify({
      endpoint: subJson.endpoint,
      keys: {
        p256dh: subJson.keys?.p256dh ?? '',
        auth: subJson.keys?.auth ?? '',
      },
    }),
  });

  if (!res.ok) throw new Error('Failed to register subscription');
}

/** Removes the superseded stored row (FR-007) — scoped server-side to `{endpoint, user_id}`. */
export async function unsubscribeAndDelete(
  subscription: PushSubscription,
  deps: PushRegistrationDeps,
): Promise<void> {
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();

  await fetch(`${deps.apiBaseUrl}/push-subscriptions`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${deps.accessToken}`,
    },
    body: JSON.stringify({ endpoint }),
  });
}

/**
 * The `superseded`/`indeterminate` sequence both callers share: unsubscribe, delete the stale
 * row, subscribe fresh, register it.
 */
export async function repairSubscription(
  subscription: PushSubscription,
  registration: ServiceWorkerRegistration,
  publishedKey: string,
  deps: PushRegistrationDeps,
): Promise<void> {
  await unsubscribeAndDelete(subscription, deps);
  await subscribeAndRegister(registration, publishedKey, deps);
}
